export type SfxName =
  | "boost"
  | "click"
  | "collision"
  | "countdown"
  | "finish"
  | "jump"
  | "ready"
  | "splash";

const MUSIC_KEY = "tidebreak.music-volume";
const SFX_KEY = "tidebreak.sfx-volume";

function readVolume(key: string, fallback: number): number {
  try {
    const stored = Number.parseFloat(localStorage.getItem(key) ?? "");
    return Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : fallback;
  } catch {
    return fallback;
  }
}

function storeVolume(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Discord/browser privacy settings may disable storage; audio still works.
  }
}

function midi(note: number): number {
  return 440 * 2 ** ((note - 69) / 12);
}

export class AudioDirector {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private scheduler: number | null = null;
  private nextStepAt = 0;
  private step = 0;
  private noiseBuffer: AudioBuffer | null = null;
  private started = false;
  private gestureInstalled = false;
  private readonly bpm = 116;

  musicVolume = readVolume(MUSIC_KEY, 0.45);
  sfxVolume = readVolume(SFX_KEY, 0.72);

  get isRunning(): boolean {
    return this.started && this.context?.state === "running";
  }

  installGestureUnlock(target: Document | HTMLElement = document): void {
    if (this.gestureInstalled) return;
    this.gestureInstalled = true;
    const unlock = () => {
      void this.start();
      target.removeEventListener("pointerdown", unlock, true);
      target.removeEventListener("keydown", unlock, true);
    };
    target.addEventListener("pointerdown", unlock, { capture: true, once: true });
    target.addEventListener("keydown", unlock, { capture: true, once: true });
  }

  async start(): Promise<void> {
    this.ensureGraph();
    if (!this.context) return;
    await this.context.resume();
    if (this.started) return;
    this.started = true;
    this.nextStepAt = this.context.currentTime + 0.04;
    this.scheduler = window.setInterval(() => this.schedule(), 80);
    this.schedule();
  }

  setMusicVolume(value: number, persist = true): void {
    this.musicVolume = Math.min(1, Math.max(0, value));
    if (persist) storeVolume(MUSIC_KEY, this.musicVolume);
    if (this.musicBus && this.context) {
      this.musicBus.gain.setTargetAtTime(this.musicVolume, this.context.currentTime, 0.025);
    }
    if (this.musicVolume > 0) void this.start();
  }

  setSfxVolume(value: number, persist = true): void {
    this.sfxVolume = Math.min(1, Math.max(0, value));
    if (persist) storeVolume(SFX_KEY, this.sfxVolume);
    if (this.sfxBus && this.context) {
      this.sfxBus.gain.setTargetAtTime(this.sfxVolume, this.context.currentTime, 0.02);
    }
  }

  toggleMusic(): number {
    this.setMusicVolume(this.musicVolume > 0 ? 0 : 0.45);
    return this.musicVolume;
  }

  play(name: SfxName): void {
    const context = this.context;
    const destination = this.sfxBus;
    if (!context || !destination || context.state !== "running") return;
    const now = context.currentTime;
    switch (name) {
      case "click":
        this.tone(now, 720, 0.035, "sine", 0.08, destination, 980);
        break;
      case "ready":
        this.tone(now, 440, 0.09, "triangle", 0.14, destination, 660);
        this.tone(now + 0.07, 660, 0.12, "triangle", 0.14, destination, 880);
        break;
      case "countdown":
        this.tone(now, 330, 0.12, "square", 0.1, destination, 330);
        break;
      case "jump":
        this.tone(now, 300, 0.18, "sine", 0.12, destination, 840);
        break;
      case "boost":
        this.tone(now, 180, 0.28, "sawtooth", 0.09, destination, 780);
        break;
      case "collision":
        this.noise(now, 0.11, 0.18, destination, 620);
        this.tone(now, 95, 0.13, "square", 0.08, destination, 55);
        break;
      case "splash":
        this.noise(now, 0.35, 0.22, destination, 1700);
        this.tone(now, 430, 0.28, "sine", 0.1, destination, 120);
        break;
      case "finish":
        [523, 659, 784, 1047].forEach((frequency, index) => {
          this.tone(now + index * 0.09, frequency, 0.2, "triangle", 0.13, destination);
        });
        break;
    }
  }

  destroy(): void {
    if (this.scheduler !== null) window.clearInterval(this.scheduler);
    this.scheduler = null;
    void this.context?.close();
    this.context = null;
  }

  private ensureGraph(): void {
    if (this.context) return;
    const Context = window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context) return;
    this.context = new Context();
    this.master = this.context.createGain();
    this.musicBus = this.context.createGain();
    this.sfxBus = this.context.createGain();
    this.compressor = this.context.createDynamicsCompressor();
    this.master.gain.value = 0.82;
    this.musicBus.gain.value = this.musicVolume;
    this.sfxBus.gain.value = this.sfxVolume;
    this.compressor.threshold.value = -14;
    this.compressor.knee.value = 18;
    this.compressor.ratio.value = 5;
    this.compressor.attack.value = 0.004;
    this.compressor.release.value = 0.18;
    this.musicBus.connect(this.master);
    this.sfxBus.connect(this.master);
    this.master.connect(this.compressor);
    this.compressor.connect(this.context.destination);
  }

  private schedule(): void {
    if (!this.context || !this.musicBus) return;
    const eighth = 60 / this.bpm / 2;
    while (this.nextStepAt < this.context.currentTime + 0.24) {
      this.scheduleStep(this.step, this.nextStepAt);
      this.step = (this.step + 1) % 32;
      this.nextStepAt += eighth;
    }
  }

  private scheduleStep(step: number, at: number): void {
    if (!this.musicBus) return;
    const bass = [40, 40, 47, 40, 43, 43, 47, 50, 40, 40, 47, 52, 43, 43, 38, 35];
    const note = bass[step % bass.length];
    if (step % 2 === 0 || step % 8 === 3) {
      this.tone(at, midi(note), 0.19, "triangle", 0.13, this.musicBus, midi(note - 2));
    }

    if (step % 4 === 0) {
      this.tone(at, 58, 0.11, "sine", 0.22, this.musicBus, 44);
    }
    if (step % 8 === 4) {
      this.noise(at, 0.075, 0.085, this.musicBus, 1900);
    }
    if (step % 2 === 1) {
      this.noise(at, 0.025, step % 4 === 3 ? 0.045 : 0.026, this.musicBus, 5800);
    }

    const offbeat = [64, 67, 71, 74][Math.floor(step / 8) % 4];
    if (step % 4 === 2) {
      this.tone(at, midi(offbeat), 0.095, "square", 0.035, this.musicBus, midi(offbeat + 7));
      this.tone(at, midi(offbeat + 7), 0.085, "triangle", 0.035, this.musicBus);
    }

    if (step % 16 === 15) {
      this.tone(at, midi(step < 16 ? 76 : 79), 0.22, "sine", 0.05, this.musicBus, midi(83));
    }
  }

  private tone(
    at: number,
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    destination: AudioNode,
    endFrequency = frequency,
  ): void {
    if (!this.context) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, frequency), at);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), at + duration);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), at + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.025);
  }

  private noise(
    at: number,
    duration: number,
    volume: number,
    destination: AudioNode,
    cutoff: number,
  ): void {
    if (!this.context) return;
    if (!this.noiseBuffer) {
      const length = Math.floor(this.context.sampleRate * 0.6);
      this.noiseBuffer = this.context.createBuffer(1, length, this.context.sampleRate);
      const channel = this.noiseBuffer.getChannelData(0);
      for (let index = 0; index < length; index += 1) channel[index] = Math.random() * 2 - 1;
    }
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = this.noiseBuffer;
    filter.type = "highpass";
    filter.frequency.value = cutoff;
    gain.gain.setValueAtTime(Math.max(0.0001, volume), at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    source.start(at);
    source.stop(at + duration + 0.02);
  }
}
